/* The app: the access guard, the page for the current path, focus on navigation, and the frame. */
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, type ComponentType } from 'react';
import { isNativeApp } from '@pathabroad/core/browser';
import { guard, matchRoute, NIGHT_PAGES, type PageKey } from '../lib/routes';
import { navigate, useLocation } from './nav';
import { useSession } from './session';
import { focusTitle } from './hooks';
import { useT } from './i18n';
import { AccountShell, Footer, TopBar } from './layout';
import { Alert, Button, Loading } from './ui';
import { Home } from '../pages/Home';
import { Start } from '../pages/Start';
import { AuthCallback, ResetPassword, SignIn, SignInCode, VerifyEmail } from '../pages/Auth';
import { GuardianConsent, GuardianNeeded, GuardianWithdraw } from '../pages/Guardian';
import { Account } from '../pages/Account';
import { Profile } from '../pages/Profile';
import { Inbox } from '../pages/Inbox';
import { Plans } from '../pages/Plans';
import { Extension } from '../pages/Extension';
import { Security } from '../pages/Security';
import { Data } from '../pages/Data';
import { Goodbye, NotFound } from '../pages/Misc';

// AI: the AI guide and essay feedback
import { AiCheck } from '../pages/AiCheck';

// reminders: WhatsApp and email
import { Reminders } from '../pages/Reminders';

// parents: progress page and "parent pays"
import { Family, ParentView } from '../pages/Family';
import { Pay, PayDone, PayWithdraw } from '../pages/Pay';
// tests, walled off from the real site
import { JournalTest } from '../pages/JournalTest';

/* the privacy notice and terms are long and rarely needed on first load */
const Privacy = lazy(() => import('../pages/Legal').then(m => ({ default: m.PrivacyPage })));
const Terms = lazy(() => import('../pages/Legal').then(m => ({ default: m.TermsPage })));

const PAGES: Record<PageKey, ComponentType> = {
  home: Home, start: Start, verifyEmail: VerifyEmail, authCallback: AuthCallback, resetPassword: ResetPassword,
  signin: SignIn, signinCode: SignInCode, guardianNeeded: GuardianNeeded, guardianConsent: GuardianConsent,
  guardianWithdraw: GuardianWithdraw, account: Account, profile: Profile, inbox: Inbox, plans: Plans,
  extension: Extension, security: Security, data: Data, privacy: Privacy, terms: Terms, notFound: NotFound,

  // AI: the AI guide and essay feedback
  aiCheck: AiCheck,

  // reminders: WhatsApp and email
  reminders: Reminders,

  // parents: progress page and "parent pays"
  family: Family, familyView: ParentView, pay: Pay, payDone: PayDone, payWithdraw: PayWithdraw,

  // tests, walled off from the real site
  journalTest: JournalTest,
};

export function App() {
  const { t } = useT();
  const loc = useLocation();
  const { session, farewell, setFarewell, refresh } = useSession();
  const route = matchRoute(loc.pathname);
  const g = farewell ? ({ kind: 'render' } as const) : guard(route.access, session, loc.pathname + loc.search, loc.query.get('next'), { app: isNativeApp() });

  useEffect(() => {
    if (g.kind === 'redirect') navigate(g.to, { replace: true });
  }, [g.kind, g.kind === 'redirect' ? g.to : '']); // eslint-disable-line react-hooks/exhaustive-deps

  /* after moving to another page: back to the top, and focus on its heading so screen readers announce it */
  const lastPath = useRef(loc.pathname);
  useEffect(() => {
    if (lastPath.current === loc.pathname) return;
    lastPath.current = loc.pathname;
    if (farewell && loc.pathname !== '/') setFarewell(false);
    if (loc.hash) return;
    window.scrollTo(0, 0);
    focusTitle();
  }, [loc.pathname, loc.hash, farewell, setFarewell]);

  /* the sign-up and sign-in screens are night, with the globe behind (styles.css: html.auth-night) */
  const night = NIGHT_PAGES.has(route.key) && !farewell;
  useLayoutEffect(() => { document.documentElement.classList.toggle('auth-night', night); }, [night]);

  const Page = PAGES[route.key];
  const portal = route.access === 'user' && g.kind === 'render' && !farewell;
  let content;
  if (farewell) content = <Goodbye />;
  else if (g.kind === 'render') content = <Suspense fallback={<Loading />}><Page /></Suspense>;
  else if (g.kind === 'retry') {
    content = (
      <div className="narrow">
        <h1 id="page-title" className="title" tabIndex={-1}>PathsAbroad</h1>
        <Alert>{t('session.error')} <Button variant="plain" small onClick={() => void refresh()}>{t('common.retry')}</Button></Alert>
      </div>
    );
  } else content = <Loading label={t('session.checking')} />;

  return (
    <>
      <a className="skip" href="#main" onClick={e => { e.preventDefault(); document.getElementById('main')?.focus(); }}>{t('app.skip')}</a>
      {/* account pages are part of the app: its navigation instead of the site's top bar and footer */}
      {!portal && <TopBar />}
      <main id="main" tabIndex={-1} className={portal ? 'main--portal' : undefined}>
        {/* the home page lays out its own full-width sections */}
        {route.key === 'home' && g.kind === 'render' && !farewell ? content
          : portal ? <AccountShell>{content}</AccountShell>
          : <div className="wrap">{content}</div>}
      </main>
      {!portal && <Footer />}
    </>
  );
}
